<template>
  <div class="pro-bag" :style="{width: props.isCombo ? '100%' : '1200px'}">
    <div class="bg-item" v-for="(item, index) in fuelBagList" :key="index">
      <div class="bag-heading">
        <div class="bag-title">{{getRealPrice(item.charNum)}}</div>
        <div class="discount" v-if="item.discount" >
          <div class="discountItem">
            <div class="discountItemTitle">{{getDiscount(item.discount)}}</div>
          </div>
        </div>
      </div>
      <div class="bag-price">
        <div class="bag-title-price">{{ getCurrencyTitle() }}{{ formatPrice(getItemPrice(item)) }}</div>
        <div class="bag-price-original" v-if="item.originalPrice && getCurrentCoinKey() !== 'jpy'">{{getCurrencyTitle()}}{{formatPrice(item.originalPrice)}}</div>
        <div class="bag-price-original" v-if="item.originalPrice && getCurrentCoinKey() === 'jpy'">{{formatPrice(item.originalPrice)}}{{getCurrencyTitle()}}</div>
      </div>
      <div v-if="props.isCombo" class="bag-unit-price">{{ getUnitPrice(item) }}</div>
      <div class="bag-item-btn" @click="onSubscribe(item)">
        <div class="bag-item-btn-title">{{$t("pro_business_buy_btn_title")}}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, defineEmits} from "vue";
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import Toast from "@/utils/toast";
import clipboardCopy from "clipboard-copy";
import ProDialog from "../ProDialog/ProDialog.vue";
// import {shareWebsiteGoogleStatistics} from "@/config/googleStatistics"
import useUserStore from "@/store/modules/user";
import useProStore from "@/store/modules/pro";
import {storeToRefs} from "pinia";
import { getProBusinessFuelBagConfig } from "@/config/proConfig"
import type { FuelBagConfigItem } from "@/types/pro"

const userStore = useUserStore();
const proStore = useProStore();
const { userInfo, token, bizTeamInfo } = storeToRefs(userStore);
const { proFuelBagConfig } = storeToRefs(proStore);
const router = useRouter()
const route = useRoute()
const fuelBagList: FuelBagConfigItem[] = ref([])
const proDialogRef = inject('proDialogRef') as Ref<InstanceType<typeof ProDialog>>
interface Props {
  isCombo?: boolean
  coin?: string
  pricingMode?: 'local' | 'global'
}
const props = defineProps<Props>()
let call = defineEmits(["onSubscribe"])
const {t, locale} = useI18n()
const numberLocale = computed(() => ({ zh: 'zh-CN', tw: 'zh-TW', 'zh-tw': 'zh-TW', itb: 'it' } as Record<string, string>)[locale.value.toLowerCase()] || locale.value)

watch(
  () => props.coin,
  () => {
    getFuelBagList(props.coin)
  }
)

watch(
  () => props.pricingMode,
  () => {
    getFuelBagList(props.coin)
  }
)

const getCurrencyTitle = () => {
  let symbol = ''
  if (props.coin) {
    symbol = userStore.getCurrentCoinSymbol(props.coin)
  } else {
    symbol = userStore.getCurrentLangSymbol(locale.value)
  }
  return symbol === '￥' ? '¥' : symbol
}

const getCurrentCoinKey = () => {
  return String(props.coin || userStore.getCurrentCoin(locale.value)).toLowerCase()
}

const getItemPrice = (item: FuelBagConfigItem) => {
  return Number(bizTeamInfo.value.agentUid ? item.bizAgentRealPrice : item.realPrice)
}

const formatPrice = (value: number | string | undefined) => {
  const price = Number(value)
  if (!Number.isFinite(price)) return String(value || '')
  return price.toLocaleString(numberLocale.value, { maximumFractionDigits: 2 })
}

const getUnitPrice = (item: FuelBagConfigItem) => {
  const charNum = Number(item.charNum || 0)
  const price = getItemPrice(item)
  if (!Number.isFinite(charNum) || charNum <= 0 || !Number.isFinite(price)) return '—'

  const isCny = getCurrentCoinKey() === 'cny'
  const unitChars = isCny ? 10000 : 1000000
  const unitPrice = price / (charNum / unitChars)
  const digits = isCny ? 2 : 1
  const count = new Intl.NumberFormat(numberLocale.value, { notation: 'compact', maximumFractionDigits: 2 }).format(unitChars)
  const roundedUnitPrice = Number(unitPrice.toFixed(digits))
  const priceLabel = new Intl.NumberFormat(numberLocale.value, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(roundedUnitPrice)
  return t('enterprise_ui_pricing_unit_price', { price: `${getCurrencyTitle()}${priceLabel}`, count })
}

const getRealPrice = (price?: string) => {
  if(price) {
    let numPrice = parseInt(price) || 0
    const count = new Intl.NumberFormat(numberLocale.value, { notation: 'compact', maximumFractionDigits: 2 }).format(numPrice)
    return t('enterprise_ui_pricing_characters', { count })
  }
  return '0'
}

const getDiscount = (discount?: string) => {
  return discount ? t('enterprise_ui_pricing_discount', { discount: discount.replace(/%+$/, '') }) : ''
}

const viewport = useViewport();
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))

function onSubscribe(item) {
  console.log("onSubscribe", item, proDialogRef)
  if (!isDesktop.value) {
    Toast(t('go_web_version'))
    return
  }
  proDialogRef.value?.open({...item, mode: 1, pricingMode: props.pricingMode || 'local'})
}

watch(userInfo, (newVal)=>{
  if(newVal.bizTeamId){
    getFuelBagList(props.coin)
  }
})

watch(proFuelBagConfig, (newVal)=>{
  getFuelBagList(props.coin)
})

function getFuelBagList(coin?: string) {
  let coinKey = ''
  if (coin) {
    coinKey = coin.toLocaleLowerCase()
  } else {
    coinKey = userStore.getCurrentCoin(locale.value)
  }
  fuelBagList.value = getProBusinessFuelBagConfig(coinKey, props.pricingMode || 'local')
}

onMounted(()=>{
  if (props.coin) {
    getFuelBagList(props.coin)
  } else {
    getFuelBagList()
  }
})
</script>

<style lang="scss" scoped>
.combo-fuel-bag {
  width: 100%;
}
.pro-bag{
  flex-direction: row;
  // align-items: center;
  display: flex;
  position: relative;
  width: 1200px;
  display: flex;
  flex-wrap: wrap;
  gap: 30px; /* 设置间距为20px */

  .bg-item {
    width: 378px;
    min-height: 242px;
    background-color: #FFFFFF;
    border-radius: 4px;
    border: 1px solid #BFBFBF;
    display: flex;
    flex-direction: column;
    padding: 30px;
    position: relative;
    &:hover {
      border: 1px solid #2590E1;
    }
    .bag-title {
      color: #262626;
      font-size: 24px;
    }
    .bag-price {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 30px;
      .bag-title-price {
        color: #2590E1;
        font-size: 36px;
        font-weight: bold;
        line-height: 1.5;
        margin-bottom: auto; /* 将第二段推到底部 */
      }
      .bag-price-original {
        color: #BFBFBF;
        font-size: 16px;
        line-height: 16px;
        text-align: left;
        font-style: normal;
        text-decoration-line: line-through;
        text-transform: none;   
        margin-top: 10px;
        margin-left: 10px;
      }
    }
    .bag-item-btn {
      width: 100%;
      height: 46px;
      background: #FFFFFF;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #BFBFBF;
      margin-top: 30px;
      cursor: pointer;
      &:hover {
        border: 1px solid #2590E1;
        opacity: 0.6;
      }
      &:hover .bag-item-btn-title {
        color: #2590E1;
      }
      .bag-item-btn-title {
        font-size: 16px;
        color: #595959;
      }
    }
    .discount {
      position: absolute;
      right: 10px;
      top: 10px;
      display: flex;
      min-width: 62px;
      height: 24px;
      background-color: #E14C25;
      border-radius: 4px;
      color: white;
      .discountItem {
        min-width: 62px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        padding: 0 5px;
        .discountItemTitle {
          // color: white;
          font-size: 12px;
        }
      }

      .triangle {
        position: relative;
        width: 111px; /* 或者您需要的宽度 */
        height: 40px; /* 矩形的高度 */
        background-color: #E14C25; /* 矩形的背景色 */
      }
      .triangle:after {
        position: absolute;
        content: '';
        width: 0px;
        height: 0px;
        background: #E14C25;
        top: 0px;
        right: 0px;
        border-right: 10px solid rgba(255, 255, 255, 1.0); /* 左边边框生成三角形，半透明的白色 */
        border-top: 20px solid transparent; /* 上边边框透明，形成凹槽效果 */
        border-bottom: 20px solid transparent; /* 下边边框透明 */
      }
    }
  }
}
</style>
